import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import backendApi from '../services/backendApi';

const SITE_VALIDATION_CACHE_TTL_MS = 5 * 60 * 1000;
const siteValidationCache = new Map<string, { valid: boolean; redirectTo: string | null; expiresAt: number }>();

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that ensures only authenticated users can access protected pages
 * User validation is handled by AuthContext to avoid duplicate checks
 * Redirects to login page if user is not authenticated
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const { site } = useParams<{ site?: string }>();
  const [siteValidationLoading, setSiteValidationLoading] = useState(false);
  const [siteValidationResult, setSiteValidationResult] = useState<{ valid: boolean; redirectTo: string | null }>({
    valid: true,
    redirectTo: null,
  });

  useEffect(() => {
    let mounted = true;

    const validateSiteContext = async () => {
      const normalizedSite = String(site || '').trim().toUpperCase();

      if (!isAuthenticated || !user || !normalizedSite) {
        if (mounted) {
          setSiteValidationResult({ valid: true, redirectTo: null });
          setSiteValidationLoading(false);
        }
        return;
      }

      const cacheKey = `${String(user.userId || user.username || 'anon')}|${normalizedSite}`;
      const cached = siteValidationCache.get(cacheKey);
      if (cached && Date.now() < cached.expiresAt) {
        if (mounted) {
          setSiteValidationResult({ valid: cached.valid, redirectTo: cached.redirectTo });
          setSiteValidationLoading(false);
        }
        return;
      }

      setSiteValidationLoading(true);

      try {
        const response = await backendApi.getSites(false);
        const sites = Array.isArray(response?.sites) ? response.sites : [];

        if (!mounted) {
          return;
        }

        if (sites.length === 0) {
          localStorage.removeItem('currentSite');
          sessionStorage.removeItem('tenant');
          sessionStorage.removeItem('site_id');
          setSiteValidationResult({ valid: false, redirectTo: '/select-site' });
          return;
        }

        const matchedSite = sites.find(
          (item: any) => String(item?.site_code || '').trim().toUpperCase() === normalizedSite
        );

        if (!matchedSite) {
          siteValidationCache.set(cacheKey, {
            valid: false,
            redirectTo: '/select-site',
            expiresAt: Date.now() + SITE_VALIDATION_CACHE_TTL_MS,
          });
          setSiteValidationResult({ valid: false, redirectTo: '/select-site' });
          return;
        }

        localStorage.setItem('currentSite', normalizedSite);
        sessionStorage.setItem('tenant', normalizedSite);
        if (matchedSite.site_id !== undefined && matchedSite.site_id !== null) {
          sessionStorage.setItem('site_id', String(matchedSite.site_id));
        }

        siteValidationCache.set(cacheKey, {
          valid: true,
          redirectTo: null,
          expiresAt: Date.now() + SITE_VALIDATION_CACHE_TTL_MS,
        });

        setSiteValidationResult({ valid: true, redirectTo: null });
      } catch (error) {
        // If site validation API is temporarily unavailable, keep user on page.
        if (mounted) {
          setSiteValidationResult({ valid: true, redirectTo: null });
        }
      } finally {
        if (mounted) {
          setSiteValidationLoading(false);
        }
      }
    };

    validateSiteContext();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, user, site]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-[#F2F8FC]/30 dark:from-[#0B1120] dark:to-[#0F172A]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-300 font-medium">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    // Save the attempted URL so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!siteValidationResult.valid && siteValidationResult.redirectTo) {
    return <Navigate to={siteValidationResult.redirectTo} state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;

